import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Product {
  id: string;
  name: string;
  category: string | null;
  base_price: number;
  tier: string | null;
  is_active: boolean;
}

interface ProductSelectionStepProps {
  products: Product[] | undefined;
  selectedProductId: string | null;
  onSelectProduct: (productId: string) => void;
  error?: string;
}

export function ProductSelectionStep({ 
  products, 
  selectedProductId, 
  onSelectProduct,
  error 
}: ProductSelectionStepProps) {
  const activeProducts = products?.filter(p => p.is_active) || [];

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        Select the product you want to sell. AI will initialize messaging, ICP matching, and pricing anchors.
      </p>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      {activeProducts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No active products found. Please create a product first.</p>
        </div>
      ) : (
        <div className="grid gap-3 max-h-[300px] overflow-y-auto">
          {activeProducts.map(product => (
            <Card 
              key={product.id}
              className={cn(
                "cursor-pointer transition-all hover:border-primary",
                selectedProductId === product.id && "border-primary bg-primary/5 ring-2 ring-primary/20"
              )}
              onClick={() => onSelectProduct(product.id)}
            >
              <CardHeader className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{product.name}</CardTitle>
                    <CardDescription>{product.category || 'Uncategorized'}</CardDescription>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">${product.base_price}</span>
                    <Badge variant="outline" className="ml-2">{product.tier || 'standard'}</Badge>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
      
      {selectedProductId && (
        <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg animate-fade-in">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm">AI is ready to optimize sales for this product</span>
        </div>
      )}
    </div>
  );
}
